import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Plus, Building2, User, FileText, ChevronRight, CreditCard } from "lucide-react";
import { AddCompanyDrawer } from "./AddCompanyDrawer";
import { AddContactDrawer } from "./AddContactDrawer";
import { AddBuyerDrawer } from "./AddBuyerDrawer";
import { OnePagerImportModal } from "@/components/import/OnePagerImportModal";
import { AddContactModal } from "./AddContactModal";

export function AddMenu() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [companyDrawerOpen, setCompanyDrawerOpen] = useState(false);
  const [contactDrawerOpen, setContactDrawerOpen] = useState(false);
  const [buyerDrawerOpen, setBuyerDrawerOpen] = useState(false);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [addContactModalOpen, setAddContactModalOpen] = useState(false);
  const [buyerSubmenuOpen, setBuyerSubmenuOpen] = useState(false);

  const handleCompanyClick = () => {
    setMenuOpen(false);
    setCompanyDrawerOpen(true);
  };

  const handleManualContactClick = () => {
    setMenuOpen(false);
    setContactDrawerOpen(true);
  };

  const handleBuyerManualClick = () => {
    setMenuOpen(false);
    setBuyerSubmenuOpen(false);
    setBuyerDrawerOpen(true);
  };

  const handleBuyerImportClick = () => {
    setMenuOpen(false);
    setBuyerSubmenuOpen(false);
    setImportModalOpen(true);
  };

  const handleScanCardClick = () => {
    setMenuOpen(false);
    setAddContactModalOpen(true);
  };

  const handleContactClick = () => {
    setMenuOpen(false);
    setAddContactModalOpen(true);
  };

  return (
    <>
      <Popover open={menuOpen} onOpenChange={setMenuOpen}>
        <PopoverTrigger asChild>
          <Button 
            className="gap-2 gold-gradient text-primary-foreground font-semibold shadow-md hover:shadow-lg transition-all btn-gold"
            data-testid="global-add-button"
          >
            <Plus className="w-4 h-4" />
            Add
          </Button>
        </PopoverTrigger>
        <PopoverContent 
          align="end" 
          className="w-[280px] p-2"
          data-testid="add-menu-popover"
        >
          <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground px-3 py-2">
            + Add New
          </div>
          <div className="space-y-1">
            {/* Mobile only — business card scanning */}
            <button
              onClick={handleScanCardClick}
              className="w-full flex items-start gap-3 p-3 rounded-lg hover:bg-primary/5 transition-colors text-left group border-b border-border pb-3 mb-1 md:hidden"
              data-testid="add-menu-scan-card"
            >
              <div className="w-10 h-10 rounded-lg gold-gradient flex items-center justify-center">
                <CreditCard className="w-5 h-5 text-primary-foreground" />
              </div>
              <div>
                <p className="font-semibold text-foreground">Scan Business Card</p>
                <p className="text-sm text-muted-foreground">AI-powered contact import</p>
              </div>
            </button>

            <Popover open={buyerSubmenuOpen} onOpenChange={setBuyerSubmenuOpen}>
              <PopoverTrigger asChild>
                <button
                  className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-primary/5 transition-colors text-left group"
                  data-testid="add-menu-buyer"
                >
                  <div className="w-10 h-10 rounded-lg bg-secondary/20 flex items-center justify-center group-hover:bg-secondary/30 transition-colors">
                    <Building2 className="w-5 h-5 text-secondary" />
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-foreground">Buyer</p>
                    <p className="text-sm text-muted-foreground">Add to buyer network</p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-muted-foreground" />
                </button>
              </PopoverTrigger>
              <PopoverContent 
                side="left" 
                align="start" 
                className="w-[220px] p-2"
                data-testid="buyer-submenu"
              >
                <button
                  onClick={handleBuyerManualClick}
                  className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-primary/5 transition-colors text-left group"
                  data-testid="add-buyer-manual"
                >
                  <Building2 className="w-5 h-5 text-muted-foreground group-hover:text-foreground" />
                  <div>
                    <p className="font-medium text-foreground">Add Manually</p>
                    <p className="text-xs text-muted-foreground">Enter buyer details</p>
                  </div>
                </button>
                <button
                  onClick={handleBuyerImportClick}
                  className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-primary/5 transition-colors text-left group"
                  data-testid="add-buyer-import"
                >
                  <FileText className="w-5 h-5 text-muted-foreground group-hover:text-foreground" />
                  <div>
                    <p className="font-medium text-foreground">Import from One-Pager</p>
                    <p className="text-xs text-muted-foreground">AI-powered extraction</p>
                  </div>
                </button>
              </PopoverContent>
            </Popover>

            <button
              onClick={handleCompanyClick}
              className="w-full flex items-start gap-3 p-3 rounded-lg hover:bg-primary/5 transition-colors text-left group"
              data-testid="add-menu-company"
            >
              <div className="w-10 h-10 rounded-lg bg-secondary/20 flex items-center justify-center group-hover:bg-secondary/30 transition-colors">
                <Building2 className="w-5 h-5 text-secondary" />
              </div>
              <div>
                <p className="font-semibold text-foreground">Company</p>
                <p className="text-sm text-muted-foreground">Add a new organization</p>
              </div>
            </button>
            <button
              onClick={handleManualContactClick}
              className="w-full flex items-start gap-3 p-3 rounded-lg hover:bg-primary/5 transition-colors text-left group"
              data-testid="add-menu-contact-manual"
            >
              <div className="w-10 h-10 rounded-lg bg-secondary/20 flex items-center justify-center group-hover:bg-secondary/30 transition-colors">
                <User className="w-5 h-5 text-secondary" />
              </div>
              <div>
                <p className="font-semibold text-foreground">Contact</p>
                <p className="text-sm text-muted-foreground">Add a person to a company</p>
              </div>
            </button>
          </div>
        </PopoverContent>
      </Popover>

      <AddCompanyDrawer open={companyDrawerOpen} onOpenChange={setCompanyDrawerOpen} />
      <AddContactDrawer open={contactDrawerOpen} onOpenChange={setContactDrawerOpen} />
      <AddBuyerDrawer open={buyerDrawerOpen} onOpenChange={setBuyerDrawerOpen} />
      <OnePagerImportModal open={importModalOpen} onOpenChange={setImportModalOpen} />
      <AddContactModal open={addContactModalOpen} onOpenChange={setAddContactModalOpen} />
    </>
  );
}
